import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { useGetQuery, useLocalStorage } from 'hooks';
import { useStateValue } from 'providers';
import { Panel, Label, ActionBtn, CloseBtn } from 'pages/games/pinmonkey/ui';
import { ColorPicker } from 'pages/games/pinmonkey/editor/color-picker';
import { rgbToHex } from 'pages/games/pinmonkey/editor/components/colorWheel';
import { HUDWrapper, BottomRow, ColorSwatch, RecentRow, RecentSwatch } from './components/wall-hud-styles';

const API_URL = process.env.API_URL;

// theme.colors() returns an rgba(...) string — the color picker/swatch work
// in plain #rrggbb hex, so convert the theme's link color for use as the
// first-ever default drawing color (before the user has picked one).
const themeColorToHex = (rgbaString) => {
  const m = rgbaString?.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  return m ? rgbToHex(+m[1], +m[2], +m[3]) : '#2a2a2a';
};

export const WallHUD = ({ pendingPixels, onColorChange, onSaved, onError, userPosition, isPicking, onStartPick, pickedColor, isErasing, onToggleErase }) => {
  const [{ session: { user } }] = useStateValue();
  const { colors } = useTheme();
  const [activeColor, setActiveColor] = useLocalStorage('wall:lastColor', themeColorToHex(colors('link')), { raw: true });
  const [recentColors, setRecentColors] = useLocalStorage('wall:recentColors', []);
  const [expanded, setExpanded] = useState(true);
  const [isFetching, setFetching] = useState(false);

  const { data: quotaData, refetch: refetchQuota } = useGetQuery({
    url: '/wall-tiles/quota',
    enabled: true,
  });

  const remaining = quotaData?.remaining ?? 256;

  const handleColorChange = (color) => {
    setActiveColor(color);
    onColorChange?.(color);
  };

  useEffect(() => {
    if (!pendingPixels.length) return;
    const color = pendingPixels[pendingPixels.length - 1]?.color;
    if (!color) return;
    setRecentColors((prev) => [color, ...prev.filter((c) => c !== color)].slice(0, 8));
  }, [pendingPixels]);

  useEffect(() => {
    onColorChange?.(activeColor);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Eyedropper: WallLayer samples a map cell and reports the color here,
  // routed through handleColorChange so it persists the same as any other pick.
  useEffect(() => {
    if (pickedColor) handleColorChange(pickedColor);
  }, [pickedColor]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!pendingPixels.length) return;
    if (!user) {
      onError?.('Log in to save your drawing.');
      return;
    }
    // pendingPixels: [{ z, x, y, cx, cy, color }]
    const pixels = pendingPixels.map(({ z, x, y, cx, cy, color }) => ({
      tile_z: z,
      tile_x: x,
      tile_y: y,
      x: cx,
      y: cy,
      color,
    }));
    const body = { pixels };
    if (userPosition?.latitude) {
      body.lat = userPosition.latitude;
      body.lng = userPosition.longitude;
    }
    setFetching(true);
    let response, json;
    try {
      response = await fetch(`${API_URL}/wall-tiles/batch`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      json = await response.json();
    } catch {
      setFetching(false);
      onError?.('Could not save — check your connection and try again.');
      return;
    }
    setFetching(false);

    // 200 = some/all saved; 409 = every pixel in this batch was locked.
    // Either way `locked` lists the cells that didn't save so they stay pending.
    if (response.status === 200 || response.status === 409) {
      if (response.status === 200) await refetchQuota();
      const lockedCells = new Set(
        (json.locked || []).map((p) => `${p.tile_z}/${p.tile_x}/${p.tile_y}/${p.x}/${p.y}`),
      );
      onSaved?.(lockedCells);
    } else {
      // e.g. 403 too far from drawing location, 429 daily limit reached.
      // Pixels stay pending — unlike the 24h-lock case, these are retryable.
      onError?.(json?.error || 'Could not save your drawing.');
    }
  };

  return (
    <HUDWrapper>
      <Panel data-tour="wall-panel">
        <BottomRow>
          <ColorSwatch $color={activeColor} onClick={() => setExpanded((v) => !v)} style={{ cursor: 'pointer', flexShrink: 0 }} />
          <CloseBtn onClick={() => setExpanded((v) => !v)} title={expanded ? 'Collapse' : 'Expand'} style={{ flexShrink: 0 }}>
            {expanded ? '▾' : '▴'}
          </CloseBtn>
          <span style={{ flexShrink: 0 }}><Label>pixels</Label>{remaining}/256</span>
        </BottomRow>
        {expanded && (
          <ColorPicker
            color={activeColor}
            onChange={handleColorChange}
            isPicking={isPicking}
            onStartPick={onStartPick}
            isErasing={isErasing}
            onToggleErase={onToggleErase}
          />
        )}
        {expanded && recentColors.length > 0 && (
          <RecentRow>
            {recentColors.map((c) => (
              <RecentSwatch key={c} $color={c} $active={c === activeColor} onClick={() => handleColorChange(c)} />
            ))}
          </RecentRow>
        )}
        <ActionBtn
          $use
          data-tour="wall-save"
          disabled={!pendingPixels.length || isFetching}
          onClick={handleSave}
          style={{ width: '100%', marginTop: '0.4rem' }}
        >
          {isFetching ? 'saving' : `save [${pendingPixels.length}]`}
        </ActionBtn>
      </Panel>
    </HUDWrapper>
  );
};
